import React, { useState, useEffect, ReactNode } from "react";
import { useQuery } from "react-query";
import Cookies from "js-cookie";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import { BranchContext } from "./BranchContext";
import { CompanyBranch } from "../../types/branch";
import { getCompanyBranches } from "../../service/client";
import { useImpersonation } from "../impersonation-context/ImpersonationContext";

const hasValidToken = () => !!Cookies.get("accessToken");

const BRANCH_COOKIE_KEY = "orders-selected-branch-id";

interface BranchProviderProps {
  children: ReactNode;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const [currentBranch, setCurrentBranch] = useState<CompanyBranch | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const { t } = useTranslation();

  // Get impersonation state to trigger refetch when it changes
  const { impersonatedCompany } = useImpersonation();
  const impersonatedCompanyId = impersonatedCompany?.id;

  const { data: branches, isLoading, error } = useQuery<CompanyBranch[], Error>(
    ["/company-branches", impersonatedCompanyId],
    () => getCompanyBranches(),
    {
      enabled: hasValidToken(),
      retry: false,
      onError: () => {
        // Skip notification if token is gone (session expired, logout in progress)
        if (!hasValidToken()) return;
        notification.error({
          message: t("branchContextProvider.errorTitle"),
          description: t("branchContextProvider.fetchBranchesError"),
          placement: "bottomRight",
        });
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (!branches || branches.length === 0) return;

    const savedBranchId = Cookies.get(BRANCH_COOKIE_KEY);

    if (savedBranchId) {
      const savedBranch = branches.find(
        (b) => b.id?.toString() === savedBranchId?.toString()
      );
      if (savedBranch) {
        // Always update currentBranch with fresh data from branches
        setCurrentBranch(savedBranch);
        return;
      }
    }

    // If no saved branch or saved branch not found, use first branch
    setCurrentBranch(branches[0]);
    Cookies.set(BRANCH_COOKIE_KEY, branches[0].id.toString());
  }, [branches]);

  const switchBranch = (branchId: number) => {
    if (!branches) return;

    const branch = branches.find((b) => b.id === branchId);
    if (!branch) {
      notification.error({
        message: t("branchContextProvider.errorTitle"),
        description: t("branchContextProvider.branchNotFoundError"),
        placement: "bottomRight",
      });
      return;
    }

    // Set cookie immediately
    Cookies.set(BRANCH_COOKIE_KEY, branchId.toString());

    // Reload the page to force a fresh state with the new branch
    window.location.reload();
  };

  const setBranchDirectly = (branch: CompanyBranch) => {
    // Activate switching state
    setIsSwitching(true);

    // Delay branch switch to allow data to load
    setTimeout(() => {
      setCurrentBranch(branch);
      Cookies.set(BRANCH_COOKIE_KEY, branch.id.toString());

      notification.success({
        message: t("branchContextProvider.branchSwitched"),
        description: t("branchContextProvider.currentBranch", {
          name: branch.name,
        }),
        placement: "bottomRight",
        duration: 2,
      });

      // Remove switching state after another delay
      setTimeout(() => {
        setIsSwitching(false);
      }, 500);
    }, 1500);
  };

  const contextValue = {
    currentBranch,
    availableBranches: branches || [],
    switchBranch,
    setBranchDirectly,
    isLoading,
    isSwitching,
    error: error || null,
  };

  return (
    <BranchContext.Provider value={contextValue}>
      {children}
    </BranchContext.Provider>
  );
};
